<template>
  <div
    v-if="data"
    class="
      group relative z-0 flex w-full flex-col border-x border-b border-grey-200 p-6 transition-colors
      duration-75
      hover-focus:border-primary-900 hover-focus:duration-150 hover-focus:ease-in-out
    "
    :class="{ 'border-t': !isChemistry || !accentColor }"
  >
    <!-- COLORIZED TOP BAR -->
    <div
      v-if="isChemistry && accentColor"
      :class="accentColor"
      class="
        absolute -inset-x-px top-0 z-10 h-2 transition-all duration-75
        group-hover-focus:h-2.5 group-hover-focus:duration-200 group-hover-focus:ease-out
      "
    />

    <!-- HEADER -->
    <div class="flex gap-1">
      <Pictograms :data="data" />
      <div
        v-if="data.isNew"
        class="ml-auto h-8 rounded-100 bg-gradient-01 p-2 text-[1rem]/[1rem] text-white"
      >
        {{ $t('product.new') }}
      </div>
    </div>

    <!-- IMAGE -->
    <div class="mt-3 aspect-[280/210] w-full">
      <AppImage
        v-if="coverImage"
        class="aspect-[280/210] w-full object-contain"
        :alt="coverImage?.alt"
        :src="coverImage?.url"
      />
    </div>

    <!-- TEXT -->
    <div class="flex flex-col pt-6 md:pt-7">
      <div class="text-grey-300 typo-body-sm">
        {{ getTranslatedProperty(category, 'name') }}
      </div>
      <h3 class="font-semibold typo-lead">
        {{ getTranslatedProperty(data, 'name') }}
      </h3>
      <div class="text-grey-300 typo-body-sm">
        <span>{{ $t('product.sku') }} {{ data.productNumber }}</span>
      </div>
      <div
        v-if="isChemistry && productTranslatedCustomFields.PIM_PRODUCT_CUSTOM_FIELD_verkaufseinheit"
        class="mt-0.5 text-grey-300 typo-body-sm"
      >
        <span>{{ productTranslatedCustomFields.PIM_PRODUCT_CUSTOM_FIELD_verkaufseinheit }}</span>
      </div>

      <div class="mt-3 typo-body-sm" v-html="productTranslatedCustomFields.PIM_PRODUCT_CUSTOM_FIELD_aufmachertxt" />
    </div>

    <NuxtLink
      :to="productRoute"
      class="mt-6 after:absolute after:inset-0"
    />
  </div>
</template>

<script setup lang="ts">
import { getTranslatedProperty, getProductRoute, getCategoryRoute } from '@shopware/helpers'
import type { Schemas } from '#shopware'

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
})
const productPath = useGetProductPath(props.data)

const productTranslatedCustomFields = computed(() => {
  return props.data.translated?.customFields || {}
})

const localePath = useLocalePath()

const categories = inject<Schemas['Category'][] | null>('categories', null)

const category = computed<Schemas['Category'] | null>(() => {
  if (!Array.isArray(props.data?.categoryTree) || props.data?.categoryTree.length < 2) {
    return null
  }

  // The current category is the second last element in the categoryTree
  return categories?.value?.find(category => category.id === props.data.categoryTree[props.data.categoryTree.length - 2]) || null
})

const isChemistry = computed(() => {
  if (!Array.isArray(props.data?.categoryTree) || props.data?.categoryTree.length < 2) {
    return false
  }
  return props.data?.categoryTree[1] === '0195b8e86b6c721d999bb1d2ca18606b' // This is the chemistry category
})

const coverImage = computed(() => {
  if (!props.data.cover?.media) {
    return null
  }

  const { media } = props.data.cover
  return {
    id: media.id,
    alt: media.translated.alt,
    url: media.url,
  }
})

const accentColor = computed(() => {
  const term = props.data.translated.customFields?.PIM_PRODUCT_CUSTOM_FIELD_hauptanwendung
  return useColorMapping(term)
})

const productRoute = computed(() => {
  const url = `/${productPath}`

  return localePath({
    name: 'products-all',
    params: { all: url.split('/')?.filter(Boolean) },
  })
})
</script>
